/*
* Created on Jan 25, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React, {useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import * as ReactDom from "react-dom";
import {convert_error_code, isAsync} from '../../../service/common';
import SubmitButtonComponent from "../Form/SubmitButtonComponent";
import {useTranslation} from "react-i18next";

const ModalComponent = forwardRef(({title, children, isOpen, onClose, onSubmit, modalSize='large', okText, input, preSubmit, onError=null, closeText, enableAlert=false, ...props}, ref) => {
    const {t} = useTranslation();
    const [showAlert, setShowAlert] = useState(enableAlert);
    // alert-info / alert-error / alert-success / alert-warning
    const [showAlertType, setShowAlertType] = useState('info');
    const [message, setMessage] = useState('');
    const [showSubmit, setShowSubmit] = useState(props.hasOwnProperty('showSubmit') ? props.showSubmit : true);
    const [showCancel, setShowCancel] = useState(true);
    const [cancelText, setCancelText] = useState(closeText || '@Cancel@');
    const [submitText, setSubmitText] = useState(okText || '@Ok@');


    const alert = (show, type, code, message, auto_hide=0) => {
        setShowAlert(show);
        setShowAlertType(type);
        if ( type === 'error') {
            setMessage(t(convert_error_code(code, message)));
        }
        else {
            setMessage(t(message));
        }
        if (auto_hide > 0) {
            window.setTimeout(()=>{
                setShowAlert(false);
            }, auto_hide);
        }
    }

    useImperativeHandle(ref, () => ({
        alert,
        setShowSubmit,
        setCancelText,
        setSubmitText,
        setShowCancel
    }));

    const doSubmit = async (setDisable) => {
        setDisable(true);
        if (typeof onSubmit?.then == 'function') {
            onSubmit(alert).then(data=>{
                if (showAlert) {
                    alert(true, 'success', 0, t('@Success@'));
                    setDisable(false);
                }
            }).catch(err=>{
                setDisable(false);
                if (showAlert) {
                    alert(true, 'error', err.code, err.message);
                }
                if (typeof onError === 'function') {
                    onError(err, alert);
                }
            });
        }
        else {
            try {
                await onSubmit(alert);
                if (showAlert) {
                    alert(true, 'success', 0, t('@Success@'));
                    setDisable(false);
                }
            }
            catch (err) {
                console.error(err);
                setDisable(false);
                const code = err?.response?.data ? err.response.data.code : '';
                const message = err?.response?.data ? err.response.data.message : err.message;
                if (showAlert) {
                    alert(true, 'error', code, message);
                }
                if (typeof onError === 'function') {
                    onError(err, alert);
                }
            }
        }
        setDisable(false);
    }

    useEffect(()=>{
        if (isOpen === false) {
            return null;
        }
    }, []);

    return ReactDom.createPortal(
        <>
            {isOpen && <div className="screen-mask visible"/> }
            {isOpen && <div className="dialog-wrapper">
                <div className={`dialog dialog-${modalSize}`}>
                    <div className="dialog-header">
                        <div className="header-title">{title}</div>
                        <div className="close" onClick={onClose}/>
                    </div>
                    <div className="dialog-body">
                        <div className={`dialog-inner-alert alert-${showAlertType} ${showAlert ? '' : 'hide'}`}>
                            <div className="alert-content">
                                <button type="button" className="alert-close-btn" aria-hidden="true" onClick={()=>setShowAlert(false)}>×</button>
                                <div className="alert-msg">
                                    {message}
                                </div>
                            </div>
                        </div>
                        {children}
                    </div>
                    <div className="dialog-footer clear">
                        <div className="pull-right">
                            <div className="q-btn-group">
                                {showSubmit && <SubmitButtonComponent text={t(submitText)} input={input} onSubmit={async (setDisable)=> doSubmit(setDisable)} preSubmit={preSubmit}/>}
                                {showCancel && <button type="button" className="q-btn q-btn-default" onClick={onClose}>{t(cancelText)}</button>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
        </>,
        document.getElementById('modal-dialog')
    );
});

export default ModalComponent;